import {
  useGetEnrollmentHYCSSecureAccountData,
  useGetEnrollmentPasskeyCredentialOptions,
  useSubmitEnrollmentHYCSSecureAccountData,
} from '@egw/api'
import { SvgPasskeyAdded } from '@egw/assets'
import { createPasskeyCredentials, getIsMobileWithPlatformAuthenticator } from '@egw/auth'
import { Button, GenericError, IconPasskey, Link } from '@egw/components'
import { configClient } from '@egw/config'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { ArrowRightIcon } from '@heroicons/react/20/solid'
import { useEffect, useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { type Config } from '../../../config/config'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - HYCS - SecureAccount')

export const SecureAccountPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const config = configClient.getConfig() as Config

  const [creatingPasskey, setCreatingPasskey] = useState<boolean>(false)
  const [passkeyPersisted, setPasskeyPersisted] = useState<boolean>(false)

  const { data, isLoading, error } = useGetEnrollmentHYCSSecureAccountData()

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : null

  useEffect(() => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_PASSKEY_VISITED, { applicationId })
  }, [applicationId])

  const { mutate: getPasskeyCredentialOptions } = useGetEnrollmentPasskeyCredentialOptions()

  const { mutate: continueEnrollmentFlow } = useSubmitEnrollmentHYCSSecureAccountData()

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = async (): Promise<void> => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_PASSKEY_CREATE_CLICKED, { applicationId })
    // Sets creating passkey to handle submit button state
    setCreatingPasskey(true)
    try {
      // Check if the user is on a mobile device with platform authenticator
      const isMobileWithPlatformAuthenticator = await getIsMobileWithPlatformAuthenticator()

      sendEvent(EDataLayerEventWealth.BANKHYCHYS_BROWSER_CREDENTIAL_CREATION_OPTIONS_START, { applicationId })
      // Build Passkey Credential Options
      const gettingPasskeyCredentialOptions = await getPasskeyCredentialOptions({
        isMobileWithPlatformAuthenticator,
        triggerPasskey: true,
      })

      if (
        gettingPasskeyCredentialOptions?.__typename === 'SubmitEnrollmentSuccess' &&
        gettingPasskeyCredentialOptions.enrollmentData?.passkeyPublicKeyCredentialRequestOptions
      ) {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_BROWSER_CREDENTIAL_OPTIONS_CREATION_SUCCESS, { applicationId })

        sendEvent(EDataLayerEventWealth.BANKHYCHYS_BROWSER_CREDENTIAL_CREATION_START, { applicationId })
        // Create the public key credentials based off the options created and pass ot browser
        const passkeyPublicKeyCredential = await createPasskeyCredentials(
          gettingPasskeyCredentialOptions.enrollmentData.passkeyPublicKeyCredentialRequestOptions,
        )

        sendEvent(EDataLayerEventWealth.BANKHYCHYS_BROWSER_CREDENTIAL_SUBMIT, { applicationId })
        // Persist the public key passkey credentials to the backend and continue enrollment flow
        const persistingPasskey = await continueEnrollmentFlow({
          passkeyPublicKeyCredential,
        })

        if (persistingPasskey?.__typename === 'SubmitEnrollmentSuccess') {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_BROWSER_CREDENTIAL_SUBMIT_SUCCESS, { applicationId })
          // Set the creating of passkey to false as it's no longer happening
          setCreatingPasskey(false)
          // Change screen to passkey persisted
          setPasskeyPersisted(true)
        } else {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_BROWSER_CREDENTIAL_SUBMIT_ERROR, { applicationId })
          void navigate(ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path)
        }
      } else {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_BROWSER_CREDENTIAL_OPTIONS_CREATION_ERROR, { applicationId })
        void navigate(ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path)
      }
      setCreatingPasskey(false)
    } catch (e) {
      log.error('Error submitting form', e)
      setCreatingPasskey(false)
    }
  }

  const onContinue = async (): Promise<void> => {
    try {
      const response = await continueEnrollmentFlow({
        proceedPasskey: true,
      })

      if (response?.__typename === 'SubmitEnrollmentSuccess') {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_PASSKEY_CREATED, { applicationId })
      } else {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_UNABLE_TO_CREATE_PASSKEY, { applicationId })
      }

      // If path exists and is different
      if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
        void navigate(response.path)
      } else {
        // If no path from BE, redirect to error
        void navigate(ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path)
      }
    } catch (e) {
      log.error('Error Continuing Enrollment Flow', e)
    }
  }

  return (
    <EnrollmentHYCSLayout title={t('hycs.secure_account.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && applicationId && !passkeyPersisted ? (
          <>
            <h1 className="typo-primary-2xl-light text-text-header mb-md">{t('hycs.secure_account.title')} </h1>
            <span className="typo-primary-lg-semibold text-text-header mb-lg">
              {t('hycs.secure_account.description')}
            </span>

            <IconPasskey className="w-1/3 h-fit" />

            <div className="flex flex-col gap-md mt-lg">
              <span className="typo-primary-base-semibold text-text-header">
                {t('hycs.secure_account.what_are_passkeys')}
              </span>
              <span className="typo-primary-base text-text-meta">
                {t('hycs.secure_account.what_are_passkeys_description')}
              </span>

              <span className="typo-primary-base-semibold text-text-header">
                {t('hycs.secure_account.where_passkeys_saved')}
              </span>
              <span className="typo-primary-base text-text-meta">
                {t('hycs.secure_account.where_passkeys_saved_description')}
              </span>
            </div>

            <div className="bg-sky-100 p-md flex flex-row rounded-lg justify-between mt-lg hidden md:flex">
              <div className="flex flex-row text-[#174a6f] typo-primary-base-semibold w-1/2">
                <div>{t('hycs.secure_account.bluetooth_enabled')}</div>
              </div>
              <Link
                href={config.zendeskArticles.helpCenterFAQs}
                className="flex flex-row gap-sm text-text-action w-1/2 justify-end items-center"
                testId="faq-button"
              >
                {t('hycs.secure_account.how_passkeys_work')}
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={creatingPasskey}
              isLoading={creatingPasskey}
              testId="registerPasskey_button"
              onClick={() => {
                void onSubmit()
              }}
            >
              {t('hycs.secure_account.create_passkey')}
            </Button>
          </>
        ) : null}

        {passkeyPersisted ? (
          <>
            <h1 className="typo-primary-2xl-light text-text-header mb-md">
              {t('hycs.secure_account.passkey_created_title')}
            </h1>

            <SvgPasskeyAdded className="w-1/3 h-fit" />

            <div className="flex flex-col gap-md mt-lg">
              <span className="typo-primary-base-semibold text-text-header">
                {t('hycs.secure_account.better_sign_in')}
              </span>
              <span className="typo-primary-base text-text-meta">{t('hycs.secure_account.single_tap')}</span>
              <span className="typo-primary-base-semibold text-text-header">
                {t('hycs.secure_account.manage_passkey')}
              </span>
              <span className="typo-primary-base text-text-meta">{t('hycs.secure_account.crud_description')}</span>
            </div>

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={creatingPasskey}
              isLoading={creatingPasskey}
              testId="primaryAction_button"
              onClick={() => {
                void onContinue()
              }}
            >
              {t('hycs.secure_account.continue')}
            </Button>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
